import React, { useState, useEffect } from "react";
import { observer } from "mobx-react";
import CheckBox from "Components/common/CheckBox";
import Button from "Components/common/Button";
import SearchInputByKeyword from "Components/common/SearchInputByKeyword";
import { reconnectSequencePopupState } from "./ReconnectSequencePopupState";
import { reconnectSequenceLoadingPopupState } from "./ReconnectSequenceLoadingPopupState";
import InboxImage from "./InboxImage";
import DateUtils from "Utils/DateUtils";
import { ReactComponent as FolderIcon } from "Assets/svg/folderIcon.svg";

const SequenceLoader = () => {
  return (
    <>
      {Array.from({ length: 5 }, (_, index) => (
        <div
          key={index}
          className="reconnect-sequence-item"
          style={{
            display: "flex",
            alignItems: "center",
            padding: "10px 0"
          }}
        >
          <div
            className="linear-background"
            style={{
              height: "16px",
              width: "16px",
              marginRight: "10px",
              borderRadius: "4px"
            }}
          />
          <div
            style={{
              flex: 1,
              display: "flex",
              flexDirection: "column"
            }}
          >
            <div
              className="linear-background"
              style={{
                height: "14px",
                width: "60%",
                marginBottom: "6px",
                borderRadius: "4px"
              }}
            />
            <div
              className="linear-background"
              style={{
                height: "12px",
                width: "40%",
                borderRadius: "4px"
              }}
            />
          </div>
          <div
            className="linear-background"
            style={{
              height: "12px",
              width: "80px",
              borderRadius: "4px"
            }}
          />
        </div>
      ))}
    </>
  );
};

const ReconnectSequencePopup = observer(() => {
  const [sequences, setSequences] = useState([]);
  const [filteredSequences, setFilteredSequences] = useState([]);
  const [selectedSequences, setSelectedSequences] = useState([]);
  const [selectAll, setSelectAll] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");


  const {
    email = "",
    userName = "",
    type = "gmail_oath"
  } = reconnectSequencePopupState.popupValues || {};

  useEffect(() => {
    if (reconnectSequencePopupState.showPopup) {
      const apiSequences = reconnectSequencePopupState?.popupValues?.sequences || [];

      const mappedSequences = apiSequences.map(seq => ({
        id: seq.id,
        name: seq?.name || "",
        checked: true,
        folderName: seq?.folderName || "",
        typeIcon: "folder",
        typeBadge: null,
        date: seq.createTs ? DateUtils.getDateMonthAndYear(seq.createTs) : ""
      }));

      setSequences(mappedSequences);
      setFilteredSequences(mappedSequences);
      setSelectedSequences(
        mappedSequences.map(seq => ({ id: seq.id, name: seq.name }))
      );
      setSelectAll(mappedSequences.length > 0);
      setSearchTerm("");
    }
  }, [
    reconnectSequencePopupState.showPopup,
    reconnectSequencePopupState.popupValues.sequences
  ]);

  useEffect(() => {
    if (searchTerm) {
      const filtered = sequences.filter(seq =>
        seq.name.toLowerCase().includes(searchTerm.toLowerCase())
      );
      setFilteredSequences(filtered);
    } else {
      setFilteredSequences(sequences);
    }
  }, [searchTerm, sequences]);

  const handleSearch = (value = "") => {
    setSearchTerm(value);
  };

  const handleCheckboxChange = (checked, id, name) => {
    const updatedSequences = sequences.map(seq =>
      seq.id === id ? { ...seq, checked } : seq
    );
    setSequences(updatedSequences);

    const filteredUpdated = filteredSequences.map(seq =>
      seq.id === id ? { ...seq, checked } : seq
    );
    setFilteredSequences(filteredUpdated);

    if (checked) {
      setSelectedSequences([...selectedSequences, { id, name }]);
    } else {
      setSelectedSequences(selectedSequences.filter(seq => seq.id !== id));
      setSelectAll(false);
    }
  };

  const handleSelectAll = checked => {
    setSelectAll(checked);
    const updatedSequences = sequences.map(seq => {
      const isInFiltered = filteredSequences.some(fs => fs.id === seq.id);
      return isInFiltered ? { ...seq, checked } : seq;
    });
    setSequences(updatedSequences);

    const filteredUpdated = filteredSequences.map(seq => ({
      ...seq,
      checked: checked
    }));
    setFilteredSequences(filteredUpdated);

    if (checked) {
      setSelectedSequences(
        filteredSequences.map(seq => ({
          id: seq.id,
          name: seq.name
        }))
      );
    } else {
      const filteredIds = filteredSequences.map(seq => seq.id);
      setSelectedSequences(
        selectedSequences.filter(seq => !filteredIds.includes(seq.id))
      );
    }
  };

  useEffect(() => {
    if (filteredSequences.length > 0) {
      const allFilteredChecked = filteredSequences.every(seq => seq.checked);
      setSelectAll(allFilteredChecked);
    } else {
      setSelectAll(false);
    }
  }, [filteredSequences]);

  const handleReconnect = () => {
    const ids = selectedSequences.map(seq => seq.id);
    reconnectSequencePopupState.reconnectSequences(ids);
  };

  const closePopup = () => {
    reconnectSequencePopupState.setShowPopup(false);
  };

  const totalSequences = sequences.length;



  return (
    reconnectSequencePopupState.showPopup && (
      <div className="popupWrapper reconnectSequencePopupWrapper">
        <div className="reconnect-sequence-popup common-popup">
          <div
            data-id="reconnect-sequence-popup"
            className="popup-wrap reconnectSequencePopup animated customzoomIn"
          >
            <div className="reconnect-sequence-popup-header">
              <div className="reconnect-sequence-popup-header-left">
                {!reconnectSequencePopupState.isFetching ? (
                  <InboxImage type={type} />
                ) : (
                  <div
                    className="linear-background"
                    style={{
                      height: "40px",
                      width: "40px",
                      borderRadius: "50%"
                    }}
                  />
                )}
                <div className="reconnect-sequence-popup-header-info">
                  <div className="reconnect-sequence-popup-name">
                    {!reconnectSequencePopupState.isFetching ? (
                      userName || ""
                    ) : (
                      <div
                        className="linear-background"
                        style={{
                          height: "15px",
                          width: "140px",
                          borderRadius: "4px",
                          marginBottom: "5px"
                        }}
                      />
                    )}
                  </div>
                  <div className="reconnect-sequence-popup-email">
                    {!reconnectSequencePopupState.isFetching ? (
                      email
                    ) : (
                      <div
                        className="linear-background"
                        style={{
                          height: "12px",
                          width: "180px",
                          borderRadius: "4px"
                        }}
                      />
                    )}
                  </div>
                </div>
              </div>
              <i
                className="material-icons reconnect-sequence-popup-close"
                onClick={closePopup}
              >
                close
              </i>
            </div>

            <div className="reconnect-sequence-popup-content">
              <p className="reconnect-sequence-popup-description">
                <span className="description-line-1">
                  This inbox was previously connected to{" "}
                  <span className="sequence-count-highlight">
                    {!reconnectSequencePopupState.isFetching ? (
                      `${totalSequences} sequences`
                    ) : (
                      <div
                        className="linear-background"
                        style={{
                          height: "14px",
                          width: "30px",
                          borderRadius: "4px",
                          display: "inline-block",
                          verticalAlign: "middle"
                        }}
                      />
                    )}
                  </span>
                </span>
                <br />
                <span className="description-line-2">
                  Please select the sequence you would like to reconnect this
                  inbox to
                </span>
              </p>

              <div className="reconnect-sequence-search">
                <SearchInputByKeyword
                  id="searchSequence"
                  placeholder="Search Sequence"
                  handleSearch={handleSearch}
                />
              </div>

              <div className="reconnect-sequence-select-all">
                <CheckBox
                  id="selectAll"
                  name="Select All"
                  checked={selectAll}
                  cbk={handleSelectAll}
                />
                <span className="sequence-count-text">
                  {!reconnectSequencePopupState.isFetching ? (
                    `${selectedSequences.length} sequences selected`
                  ) : (
                    <div
                      className="linear-background"
                      style={{
                        height: "14px",
                        width: "120px",
                        borderRadius: "4px",
                        display: "inline-block",
                        verticalAlign: "middle"
                      }}
                    />
                  )}
                </span>
              </div>

              <div className="reconnect-sequence-list">
                {!reconnectSequencePopupState.isFetching ? (
                  filteredSequences.length > 0 ? (
                    filteredSequences.map(sequence => {
                      const { id, name, checked, folderName, typeIcon, typeBadge, date } = sequence || {};

                      return (
                        <div
                          key={id}
                          className="reconnect-sequence-item"
                        >
                          <CheckBox
                            id={id}
                            name=""
                            checked={checked}
                            cbk={handleCheckboxChange}
                            hasCustomDom={true}
                            dom={
                              <div className="reconnect-sequence-item-content">
                                <div className="reconnect-sequence-item-main">
                                  <div className="reconnect-sequence-name">
                                    {name}
                                  </div>
                                  {folderName ? (
                                    <div className="reconnect-sequence-type">
                                      <div className="sequence-type-icon">
                                        <FolderIcon />
                                      </div>
                                      {typeBadge ? (
                                        <span className="sequence-type-badge">
                                          {typeBadge}
                                        </span>
                                      ) : null}
                                      <span className="sequence-type-text">
                                        {folderName}
                                      </span>
                                    </div>
                                  ) : null}
                                </div>
                                <div className="reconnect-sequence-date">
                                  {date}
                                </div>
                              </div>
                            }
                          />
                        </div>
                      );
                    })
                  ) : (
                    <div className="no-sequences-message">
                      No sequences found
                    </div>
                  )
                ) : (
                  <SequenceLoader />
                )}
              </div>
            </div>

            <div className="reconnect-sequence-popup-actions">
              <Button
                id="reconnectSequenceBtn"
                name="reconnectSequenceBtn"
                btnText="Reconnect"
                className={`reconnect-sequence-btn ${selectedSequences.length > 0 ? "" : "disabled"
                  }`}
                type="button"
                click={
                  selectedSequences.length > 0 ? handleReconnect : () => { }
                }
                disabled={selectedSequences.length === 0 || reconnectSequencePopupState.isReconnecting}
                loader={reconnectSequencePopupState.isReconnecting}
              />
            </div>
          </div>
        </div>
        <div className="confirmation-popup-mask" onClick={closePopup} />
      </div>
    )
  );
});

export default ReconnectSequencePopup;
export { ReconnectSequencePopup };
